import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import { Bell, Camera, Check, Hourglass, Mail, Plus, RefreshCw, Search, Trash2, Users, CameraOff } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import "./CSS/Team.css";

const API_BASE = "https://timetrakerapi.sntservices.in";
interface EmployeeDetail {
  firstName: string | null;
  email: string;
  takess: boolean;
  notification: boolean;
  screenshotTime: number;
  screenshotMinMinutes: number;
  screenshotMaxMinutes: number;
  idleTimeoutMinutes: number;
  projectId: string;
  money: number;
  withoutScreenshot: boolean;
}
interface Project { id: number; name: string | null; }
const projectIds = (value: string) => value.split(",").map(Number).filter((id) => Number.isInteger(id) && id > 0);

const Team: React.FC = () => {
  const { user } = useUser();
  const adminId = user?.id;
  const [staff, setStaff] = useState<EmployeeDetail[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [draftName, setDraftName] = useState("");
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [screenshotMinMinutes, setScreenshotMinMinutes] = useState(5);
  const [screenshotMaxMinutes, setScreenshotMaxMinutes] = useState(10);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(5);
  const [notification, setNotification] = useState(true);
  const [money, setMoney] = useState("0");
  const [withoutScreenshot, setWithoutScreenshot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refreshStaff = useCallback(async (signal?: AbortSignal) => {
    if (!adminId) return;
    const response = await axios.get<EmployeeDetail[]>(`${API_BASE}/GetEmployeeDetails`, { params: { adminid: adminId }, signal });
    if (!signal?.aborted) setStaff(Array.isArray(response.data) ? response.data : []);
  }, [adminId]);

  useEffect(() => {
    if (!adminId) { setLoading(false); return; }
    const controller = new AbortController();
    axios.get<EmployeeDetail[]>(`${API_BASE}/GetEmployeeDetails`, { params: { adminid: adminId }, signal: controller.signal })
      .then((people) => {
      setStaff(Array.isArray(people.data) ? people.data : []);
      }).catch((err) => { if (!axios.isCancel(err)) setError("Team members could not be loaded. Try refreshing the page."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    axios.get<Project[]>(`${API_BASE}/GetProject`, { params: { Adminid: adminId }, signal: controller.signal })
      .then((response) => setProjects(Array.isArray(response.data) ? response.data : []))
      .catch((err) => { if (!axios.isCancel(err)) setError("Projects could not be loaded. Try refreshing the page."); });
    return () => controller.abort();
  }, [adminId]);

  const current = staff.find((person) => person.email.toLowerCase() === selectedEmail.toLowerCase());
  const filteredStaff = useMemo(() => staff.filter((person) =>
    `${person.firstName || ""} ${person.email}`.toLowerCase().includes(search.trim().toLowerCase())
  ), [staff, search]);

  const selectPerson = (person: EmployeeDetail) => {
    setSelectedEmail(person.email);
    setDraftName(person.firstName || "");
    setSelectedProjects(projectIds(person.projectId));
    setScreenshotMinMinutes(person.screenshotMinMinutes || person.screenshotTime || 5);
    setScreenshotMaxMinutes(person.screenshotMaxMinutes || person.screenshotTime || 10);
    setIdleTimeoutMinutes(person.idleTimeoutMinutes || 5);
    setNotification(person.notification);
    setMoney(String(person.money ?? 0));
    setWithoutScreenshot(person.withoutScreenshot ?? false);
    setError("");
  };

  const startInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid staff email address."); return; }
    const existing = staff.find((person) => person.email.toLowerCase() === email);
    if (existing) { selectPerson(existing); setInviteEmail(""); return; }
    setSelectedEmail(email);
    setDraftName("");
    setSelectedProjects([]);
    setScreenshotMinMinutes(5);
    setScreenshotMaxMinutes(10);
    setIdleTimeoutMinutes(5);
    setNotification(true);
    setMoney("0");
    setWithoutScreenshot(false);
    setInviteEmail("");
    setError("");
    try {
      const response = await axios.get(`${API_BASE}/EmailIsExisting`, { params: { Email: email } });
      if (typeof response.data === "string") setDraftName(response.data);
    } catch { /* The email can still be assigned before the user registers. */ }
  };

  const save = async () => {
    if (!adminId || !selectedEmail) return;
    if (selectedProjects.length === 0) { setError("Select at least one project before saving."); return; }
    if (!Number.isInteger(Number(money)) || Number(money) < 0) { setError("Enter a valid whole-number hourly rate."); return; }
    if (screenshotMinMinutes > screenshotMaxMinutes) { setError("Minimum screenshot delay cannot be longer than the maximum delay."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/UpserEmployee`, null, { params: {
        ProjectIds: selectedProjects.join(","), AdminId: adminId, Email: selectedEmail,
        takeSS: true, screenshottime: screenshotMaxMinutes,
        screenshotMinMinutes, screenshotMaxMinutes, idleTimeoutMinutes,
        nofification: notification, money: Number(money),
        withoutScreenshot,
      } });
      await refreshStaff();
      toast.success("Team member saved.");
    } catch { setError("Could not save this team member. Please try again."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!adminId || !current) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/deleteEmp`, { params: { Adminid: adminId, ProjectId: current.projectId, email: current.email } });
      setConfirmDelete(false);
      setSelectedEmail("");
      await refreshStaff();
      toast.success("Team member removed.");
    } catch { setError("Could not remove this team member. Please try again."); }
    finally { setDeleting(false); }
  };

  const sendEmail = async () => {
    if (!adminId || !selectedEmail) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/send-email`, null, { params: { Adminid: adminId, Email: selectedEmail } });
      toast.success("Email sent successfully.");
    } catch { toast.error("Could not send email."); }
    finally { setSending(false); }
  };

  const toggleProject = (id: number) => setSelectedProjects((ids) =>
    ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]
  );

  return <main className="team-page">
    <header className="team-page-header"><div><span className="team-eyebrow">PEOPLE / SETTINGS</span><h1>Team</h1><p>Manage staff access, project assignments, and tracking preferences.</p></div><span className="team-count"><Users size={17} /> {staff.length} members</span></header>
    {error && <div className="team-alert" role="alert">{error}<button type="button" onClick={() => setError("")}>Dismiss</button></div>}
    <div className="team-layout">
      <aside className="team-list-panel">
        <div className="team-list-heading"><div><strong>Team members</strong><small>Select a person to edit</small></div><button type="button" onClick={() => refreshStaff().catch(() => setError("Could not refresh team members."))} aria-label="Refresh team members"><RefreshCw size={17} /></button></div>
        <form className="team-invite" onSubmit={startInvite}><input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="Staff email address" aria-label="Staff email address" /><button type="submit"><Plus size={16} /> Add</button></form>
        <label className="team-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name or email" aria-label="Search team members" /></label>
        <div className="team-list" role="list">{loading ? <p className="team-list-empty">Loading team…</p> : filteredStaff.length ? filteredStaff.map((person) => {
          const count = projectIds(person.projectId).length;
          return <button key={person.email} type="button" role="listitem" className={`team-person ${selectedEmail.toLowerCase() === person.email.toLowerCase() ? "is-active" : ""}`} onClick={() => selectPerson(person)}><span className="team-avatar">{(person.firstName || person.email).slice(0, 1).toUpperCase()}</span><span className="team-person-copy"><strong>{person.firstName || "Name unavailable"}</strong><small>{person.email}</small><em>{count} {count === 1 ? "project" : "projects"}</em></span></button>;
        }) : <p className="team-list-empty">{staff.length ? "No team members match your search." : "No team members added yet."}</p>}</div>
      </aside>

      <section className="team-detail-panel">{selectedEmail ? <>
        <div className="team-detail-header"><div><span className="team-eyebrow">{current ? "TEAM MEMBER" : "NEW MEMBER"}</span><h2>{draftName || "Name unavailable"}</h2><p>{selectedEmail}</p></div><div className="team-detail-actions"><button type="button" className="team-button team-button-light" onClick={sendEmail} disabled={sending}><Mail size={16} /> {sending ? "Sending…" : "Send email"}</button>{current && <button type="button" className="team-button team-button-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> Remove</button>}<button type="button" className="team-button team-button-primary" onClick={save} disabled={saving}><Check size={16} /> {saving ? "Saving…" : "Save changes"}</button></div></div>
        <div className="team-detail-body"><div className="team-section-title"><h3>Assigned projects</h3><p>Choose the projects this person can work on.</p></div><div className="team-project-grid">{projects.length ? projects.map((project) => <label className={`team-project ${selectedProjects.includes(project.id) ? "is-selected" : ""}`} key={project.id}><input type="checkbox" checked={selectedProjects.includes(project.id)} onChange={() => toggleProject(project.id)} /><span className="team-project-check">{selectedProjects.includes(project.id) && <Check size={13} />}</span><span>{project.name || "Unnamed project"}</span></label>) : <p className="team-muted">No projects available. Add a project first.</p>}</div>
          <div className="team-settings">
            <div className="team-section-title"><h3>Tracking settings</h3><p>Applied to this member’s selected projects.</p></div>
            <div className="team-settings-grid">
              <label className="team-setting"><span className="team-setting-icon"><CameraOff size={19} /></span><span><strong>Without screenshot</strong><small>Track time only — no captures taken</small></span><input type="checkbox" checked={withoutScreenshot} onChange={(event) => setWithoutScreenshot(event.target.checked)} /></label>
              <label className={`team-setting${withoutScreenshot ? " team-setting--disabled" : ""}`}><span className="team-setting-icon"><Camera size={19} /></span><span><strong>Minimum screenshot delay</strong><small>Earliest time before the next capture</small></span><select value={screenshotMinMinutes} disabled={withoutScreenshot} onChange={(event) => { const value = Number(event.target.value); setScreenshotMinMinutes(value); if (value > screenshotMaxMinutes) setScreenshotMaxMinutes(value); }}>{[1, 2, 3, 5, 10, 15, 20, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{minutes} min</option>)}</select></label>
              <label className={`team-setting${withoutScreenshot ? " team-setting--disabled" : ""}`}><span className="team-setting-icon"><Camera size={19} /></span><span><strong>Maximum screenshot delay</strong><small>Latest time before the next capture</small></span><select value={screenshotMaxMinutes} disabled={withoutScreenshot} onChange={(event) => setScreenshotMaxMinutes(Number(event.target.value))}>{[1, 2, 3, 5, 10, 15, 20, 30, 45, 60].filter((minutes) => minutes >= screenshotMinMinutes).map((minutes) => <option key={minutes} value={minutes}>{minutes} min</option>)}</select></label>
              <label className="team-setting"><span className="team-setting-icon"><Hourglass size={19} /></span><span><strong>Idle allowance</strong><small>Count this time, then pause and record idle</small></span><select value={idleTimeoutMinutes} onChange={(event) => setIdleTimeoutMinutes(Number(event.target.value))}>{[1, 2, 3, 5, 10, 15, 20, 30].map((minutes) => <option key={minutes} value={minutes}>{minutes} min</option>)}</select></label>
              <label className={`team-setting${withoutScreenshot ? " team-setting--disabled" : ""}`}><span className="team-setting-icon"><Bell size={19} /></span><span><strong>Screenshot notification</strong><small>Notify staff before capture</small></span><input type="checkbox" checked={notification} disabled={withoutScreenshot} onChange={(event) => setNotification(event.target.checked)} /></label>
              <label className="team-setting"><span className="team-setting-icon">&#x20B9;</span><span><strong>Hourly rate</strong><small>Used in reports</small></span><input className="team-money" type="number" min="0" step="1" value={money} onChange={(event) => setMoney(event.target.value)} /></label>
            </div>
          </div>
        </div>
      </> : <div className="team-detail-empty"><Users size={30} /><h2>Select a team member</h2><p>Choose someone from the list, or enter an email to add a new person.</p></div>}</section>
    </div>
    <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered><Modal.Header closeButton><Modal.Title>Remove team member?</Modal.Title></Modal.Header><Modal.Body>This removes {current?.firstName || current?.email} from all assigned projects.</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button><Button variant="danger" onClick={remove} disabled={deleting}>{deleting ? "Removing…" : "Remove"}</Button></Modal.Footer></Modal>
  </main>;
};

export default Team;
